/** VUEX module for data management in app **/

import { EmailsApi } from '@/services/api-emails.js';
import { EmailHeadersApi } from '@/services/api-email-headers.js';
import { EmailAttachmentsApi } from '@/services/api-email-attachments.js';
import { EmailEventsApi } from '@/services/api-email-events.js';

function defaultEmailDataFactory() {
  return {
    headers: '',
    attachments: [],
    events: [],
    webhooks: {},
  };
}

function defaultStateFactory() {
  return {
    // List of emails
    emails: {},

    // Individual Email
    email: {},
    ...defaultEmailDataFactory(),
  };
}

// -----------------------------------------------------------------
export const state = defaultStateFactory();

// -----------------------------------------------------------------
export const getters = {
  emails: (state) => {
    return state.emails.data || [];
  },

  pagination: (state) => {
    // destructure out the pagination data
    const { meta } = state.emails;
    return meta || {};
  },

  emailById: (state, getters) => (id) => {
    return getters.emails.find((email) => email.id === id);
  },
};

// -----------------------------------------------------------------
export const mutations = {
  setEmails(state, emails = {}) {
    state.emails = emails;
  },

  setEmail(state, email = {}) {
    state.email = email;
  },

  setHeaders(state, headers = '') {
    state.headers = headers;
  },

  setAttachments(state, attachments = []) {
    state.attachments = attachments;
  },

  setEvents(state, events = []) {
    state.events = events;
  },

  setWebhooks(state, webhooks) {
    state.webhooks = { ...state.webhooks, ...webhooks };
  },

  resetEmail(state) {
    state.email = {};
  },

  resetEmailData(state) {
    const emailData = defaultEmailDataFactory();
    for (const prop in emailData) {
      if (Object.prototype.hasOwnProperty.call(emailData, prop)) {
        state[prop] = emailData[prop];
      }
    }
  },

  resetState(state, defaultState) {
    state = defaultState;
  },
};

// -----------------------------------------------------------------
export const actions = {
  index({ commit, state }, { query }) {
    return EmailsApi.index(query)
      .then((emails) => {
        commit('setEmails', emails);
        return state.emails;
      })
      .catch((err) => {
        commit('setEmails', {});
        throw err;
      });
  },

  /**
   * Get the data for an individual email
   * @param {Object} context - Vuex context object
   * @param {String} emailId
   * @returns {Promise}
   */
  show({ commit, getters, state }, emailId) {
    // everytime we request an email, clear out any previous data.
    commit('resetEmailData');

    // 1. Check if we've already fetched this email as part of the list
    let email = getters.emailById(emailId);
    if (email) {
      commit('setEmail', email);
      return Promise.resolve(email);
    }

    // 2. Or, Fetch the email from the API.
    return EmailsApi.show(emailId)
      .then((email) => {
        commit('setEmail', email);
        return state.email;
      })
      .catch((err) => {
        commit('resetEmail');
        throw err;
      });
  },

  /**
   * Get an email's headers
   * @param {Object} context - Vuex context object
   * @param {String} emailId
   * @returns {Promise}
   */
  readHeaders({ commit, state }, emailId) {
    return EmailHeadersApi.getHeaders(emailId).then((headers) => {
      if (state.email.id === emailId) {
        commit('setHeaders', headers);
      }
    });
  },

  /**
   * Get an email's attachments
   * @param {Object} context - Vuex context object
   * @param {String} emailId
   * @returns {Promise}
   */
  readAttachments({ commit, state }, emailId) {
    return EmailAttachmentsApi.getAttachments(emailId).then((attachments) => {
      if (Array.isArray(attachments) && state.email.id === emailId) {
        commit('setAttachments', attachments);
      }
    });
  },

  /**
   * Get an email's events
   * @param {Object} context - Vuex context object
   * @param {String} emailId
   * @returns {Promise}
   */
  readEmailEvents({ commit, state }, emailId) {
    return EmailEventsApi.getEvents(emailId).then((events) => {
      if (state.email.id === emailId) {
        commit('setEvents', events);
      }
    });
  },

  /**
   * Get an event's webhooks
   * @param {Object} context - Vuex context object
   * @param {Object} payload
   * @param {String} payload.emailId
   * @param {String} payload.eventId
   * @returns {Promise}
   */
  readEventWebooks({ commit, state }, { emailId, eventId }) {
    if (eventId in state.webhooks) {
      return Promise.resolve();
    }

    return EmailEventsApi.getWebhooks(emailId, eventId).then((webhooks) => {
      if (state.email.id === emailId) {
        let webhooksbyEvent = {};
        webhooksbyEvent[eventId] = webhooks;
        commit('setWebhooks', webhooksbyEvent);
      }
    });
  },

  retryParsing(context, emailId) {
    return EmailsApi.store(emailId).then((newEmailId) => {
      return newEmailId;
    });
  },

  /**
   * Sets the state back to the default values
   * @returns {Promise}
   */
  reset({ commit }) {
    commit('resetState', defaultStateFactory());
  },
};
